import { eventDispacher } from "./eventDispatcher";
import {
  ProgressUpdateEvent,
  type SignalRData,
  BalanceUpdateEvent,
} from "./baseEventType";
import {
  ContentTypeEnum,
  type MessageData,
  MessageSeverity,
  type ParsedSignalRData,
  MessageTypeEnum,
} from "../types/signaler.events.types.ts";

export class SignalREventHandler {
  private processedCorrelationIds = new Set<string>();

  public handleReceiveMessage = (
    message: string,
    data: SignalRData,
    onMessagePopup: (messageData: MessageData) => void
  ): void => {
    try {
      const parsedData = this.parseSignalRData(data);
      if (this.isDuplicateMessage(parsedData.CorrelationId)) {
        return;
      }

      this.markMessageAsProcessed(parsedData.CorrelationId);
      this.handleMessagePopup(message, parsedData, onMessagePopup);
      this.handleProgressUpdate(parsedData);
      this.handleGameBalanceUpdate(parsedData);
    } catch (error) {
      console.warn("Failed to process incoming SignalR message:", error);
    }
  };

  private parseSignalRData(data: unknown): ParsedSignalRData {
    if (typeof data === "string") {
      try {
        return JSON.parse(data) as ParsedSignalRData;
      } catch {
        return {};
      }
    }
    return (data as ParsedSignalRData) ?? {};
  }

  private isDuplicateMessage(correlationId?: string): boolean {
    return correlationId
      ? this.processedCorrelationIds.has(correlationId)
      : false;
  }

  private markMessageAsProcessed(correlationId?: string): void {
    if (correlationId) {
      this.processedCorrelationIds.add(correlationId);
    }
  }

  private handleMessagePopup(
    message: string,
    parsedData: ParsedSignalRData,
    onMessagePopup: (messageData: MessageData) => void
  ): void {
    if (parsedData.PopUpMessage !== true) {
      return;
    }

    const messageContent =
      parsedData.Message ?? message ?? "New message received";
    const messageType = this.mapSeverityToType(parsedData.Severity);
    const contentType = this.mapContentType(parsedData.ContentType);
    onMessagePopup({
      message: messageContent,
      type: messageType,
      contentType,
      localizedMessages: parsedData.localizedMessages,
    });
  }

  private mapSeverityToType(
    severity?: MessageSeverity
  ): "info" | "warning" | "error" | "success" {
    switch (severity) {
      case MessageSeverity.Info:
        return "info";
      case MessageSeverity.Warning:
        return "warning";
      case MessageSeverity.Error:
        return "error";
      case MessageSeverity.Success:
        return "success";
      default:
        return "info";
    }
  }

  private mapContentType(contentType?: ContentTypeEnum): "text" | "html" {
    switch (contentType) {
      case ContentTypeEnum.Text:
        return "text";
      case ContentTypeEnum.Html:
        return "html";
      default:
        return "text";
    }
  }

  private handleProgressUpdate(parsedData: ParsedSignalRData): void {
    if (
      parsedData.MessageType === MessageTypeEnum.AggregationProgress &&
      parsedData.AggregationId &&
      parsedData.CurrentScore != null
    ) {
      console.log('📊 Dispatching ProgressUpdateEvent:', {
        AggregationId: parsedData.AggregationId,
        CurrentScore: parsedData.CurrentScore,
        KeyId: parsedData.KeyId,
        MessageType: parsedData.MessageType
      });
      eventDispacher.emit(
        new ProgressUpdateEvent(
          parsedData.AggregationId,
          parsedData.CurrentScore,
          parsedData.KeyId ?? "default_key",
          (parsedData.ContentType ?? ContentTypeEnum.Text).toString(),
          parsedData.MessageType
        )
      );
    }
  }

  private handleGameBalanceUpdate(parsedData: ParsedSignalRData): void {
    if (
      parsedData.MessageType === MessageTypeEnum.BalanceChange &&
      parsedData.Amount != null
    ) {
      console.log('💰 Dispatching BalanceUpdateEvent:', {
        Amount: parsedData.Amount,
        CoinId: parsedData.CoinId,
        AddedAmount: parsedData.AddedAmount,
        AggregationId: parsedData.AggregationId,
        MessageType: parsedData.MessageType
      });
      eventDispacher.emit(
        new BalanceUpdateEvent(
          parsedData.Amount,
          parsedData.ContentType?.toString() || "1",
          parsedData.MessageType,
          parsedData.AggregationId,
          parsedData.CoinId,
          parsedData.AddedAmount
        )
      );
    }
  }

  public clearProcessedIds(): void {
    this.processedCorrelationIds.clear();
  }

  public getProcessedCount(): number {
    return this.processedCorrelationIds.size;
  }
}
